'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { authClient } from '@/auth/client';
import { EkutuQrLogo } from '@/components/EkutuQrLogo';
import {
	Mail,
	Lock,
	KeyRound,
	Eye,
	EyeOff,
	ShieldCheck,
	ArrowRight,
	ArrowLeft,
	Loader2,
	AlertCircle,
	CheckCircle2,
} from 'lucide-react';

export function ForgotPasswordForm() {
	const [email, setEmail] = useState('');
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);

	async function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setBusy(true);
		setError('');
		setMessage('');
		const result = await authClient.requestPasswordReset({
			email: email.trim(),
			redirectTo: '/reset-password',
		});
		setBusy(false);
		if (result.error) setError(result.error.message || 'Unable to send the reset email.');
		else setMessage('If an account exists for this email, a password reset link has been sent.');
	}

	return (
		<main className="relative min-h-[calc(100vh-140px)] flex items-center justify-center px-4 py-12 sm:py-16 overflow-hidden bg-gradient-to-b from-slate-50 via-emerald-50/20 to-slate-100/60">
			{/* Ambient aurora glow elements */}
			<div className="pointer-events-none absolute -top-40 -left-40 size-[450px] rounded-full bg-emerald-400/15 blur-3xl" />
			<div className="pointer-events-none absolute -bottom-40 -right-40 size-[450px] rounded-full bg-teal-500/10 blur-3xl" />

			<div className="relative z-10 w-full max-w-md">
				{/* Frosted Aurora Card */}
				<div className="rounded-3xl bg-white/85 backdrop-blur-xl border border-white/80 shadow-xl shadow-slate-200/50 p-6 sm:p-9 transition-all">
					{/* Logo & Header */}
					<div className="text-center mb-6">
						<Link
							href="/"
							className="inline-block transition-transform hover:scale-105 active:scale-95 mb-3"
							title="Ekutu QR"
						>
							<EkutuQrLogo size="default" />
						</Link>

						<div className="flex justify-center mb-3">
							<span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200/80 text-emerald-800 text-[11px] font-semibold shadow-2xs">
								<KeyRound className="size-3.5 text-emerald-600" />
								Account Recovery
							</span>
						</div>

						<h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
							Reset your password
						</h1>
						<p className="mt-1.5 text-xs sm:text-sm text-slate-500">
							Enter your registered email address and we'll send you a secure link to reset your
							password.
						</p>
					</div>

					<form className="space-y-4" onSubmit={submit}>
						<div>
							<label className="block text-xs font-semibold text-slate-700 mb-1.5">
								Email address
							</label>
							<div className="relative">
								<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
									<Mail className="size-4" />
								</div>
								<input
									className="w-full rounded-xl border border-slate-200 bg-white/70 hover:bg-white pl-10 pr-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0f766e] focus:ring-2 focus:ring-[#0f766e]/20 transition-all"
									placeholder="you@example.com"
									type="email"
									autoComplete="email"
									required
									value={email}
									onChange={(event) => setEmail(event.target.value)}
								/>
							</div>
						</div>

						{/* Error Banner */}
						{error && (
							<div
								role="alert"
								className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200/80 p-3 text-xs text-red-700"
							>
								<AlertCircle className="size-4 text-red-500 shrink-0 mt-0.5" />
								<span>{error}</span>
							</div>
						)}

						{/* Status/Success Message */}
						{message && (
							<div
								role="status"
								className="flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200/80 p-3 text-xs text-emerald-800"
							>
								<CheckCircle2 className="size-4 text-emerald-600 shrink-0 mt-0.5" />
								<span>{message}</span>
							</div>
						)}

						{/* Submit button */}
						<button
							className="w-full py-3 px-4 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#0f766e] to-[#047857] hover:from-[#115e59] hover:to-[#065f46] shadow-sm hover:shadow-md transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer mt-2"
							type="submit"
							disabled={busy}
						>
							{busy ? (
								<>
									<Loader2 className="size-4 animate-spin" />
									<span>Sending reset link…</span>
								</>
							) : (
								<>
									<span>Send Reset Link</span>
									<ArrowRight className="size-4" />
								</>
							)}
						</button>

						{/* Back link */}
						<div className="pt-2 text-center">
							<Link
								href="/sign-in"
								className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors"
							>
								<ArrowLeft className="size-3.5" />
								<span>Back to sign in</span>
							</Link>
						</div>
					</form>

					{/* Trust footer */}
					<div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-center gap-3 text-[11px] text-slate-600 font-medium">
						<span className="flex items-center gap-1">
							<ShieldCheck className="size-3.5 text-emerald-600" />
							<span>256-bit SSL</span>
						</span>
						<span className="text-slate-300">•</span>
						<span>Ekutu QR Recovery</span>
					</div>
				</div>
			</div>
		</main>
	);
}

export function ResetPasswordForm({ token }: { token?: string }) {
	const router = useRouter();
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);

	async function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!token)
			return setError('This password reset link is missing its token. Request a new one.');
		setBusy(true);
		setError('');
		const result = await authClient.resetPassword({ newPassword: password, token });
		setBusy(false);
		if (result.error)
			setError(result.error.message || 'This reset link has expired. Request a new one.');
		else router.replace('/sign-in?reset=1');
	}

	return (
		<main className="relative min-h-[calc(100vh-140px)] flex items-center justify-center px-4 py-12 sm:py-16 overflow-hidden bg-gradient-to-b from-slate-50 via-emerald-50/20 to-slate-100/60">
			{/* Ambient aurora glow elements */}
			<div className="pointer-events-none absolute -top-40 -left-40 size-[450px] rounded-full bg-emerald-400/15 blur-3xl" />
			<div className="pointer-events-none absolute -bottom-40 -right-40 size-[450px] rounded-full bg-teal-500/10 blur-3xl" />

			<div className="relative z-10 w-full max-w-md">
				{/* Frosted Aurora Card */}
				<div className="rounded-3xl bg-white/85 backdrop-blur-xl border border-white/80 shadow-xl shadow-slate-200/50 p-6 sm:p-9 transition-all">
					{/* Logo & Header */}
					<div className="text-center mb-6">
						<Link
							href="/"
							className="inline-block transition-transform hover:scale-105 active:scale-95 mb-3"
							title="Ekutu QR"
						>
							<EkutuQrLogo size="default" />
						</Link>

						<div className="flex justify-center mb-3">
							<span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200/80 text-emerald-800 text-[11px] font-semibold shadow-2xs">
								<ShieldCheck className="size-3.5 text-emerald-600" />
								Choose New Password
							</span>
						</div>

						<h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
							Create new password
						</h1>
						<p className="mt-1.5 text-xs sm:text-sm text-slate-500">
							Choose a strong password to secure your Ekutu QR account.
						</p>
					</div>

					<form className="space-y-4" onSubmit={submit}>
						<div>
							<label className="block text-xs font-semibold text-slate-700 mb-1.5">
								New password
							</label>
							<div className="relative">
								<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
									<Lock className="size-4" />
								</div>
								<input
									className="w-full rounded-xl border border-slate-200 bg-white/70 hover:bg-white pl-10 pr-10 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0f766e] focus:ring-2 focus:ring-[#0f766e]/20 transition-all"
									placeholder="Minimum 8 characters"
									type={showPassword ? 'text' : 'password'}
									autoComplete="new-password"
									minLength={8}
									required
									value={password}
									onChange={(event) => setPassword(event.target.value)}
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600 cursor-pointer"
									title={showPassword ? 'Hide password' : 'Show password'}
								>
									{showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
								</button>
							</div>
						</div>

						{/* Error Banner */}
						{error && (
							<div
								role="alert"
								className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200/80 p-3 text-xs text-red-700"
							>
								<AlertCircle className="size-4 text-red-500 shrink-0 mt-0.5" />
								<span>{error}</span>
							</div>
						)}

						{/* Submit button */}
						<button
							className="w-full py-3 px-4 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#0f766e] to-[#047857] hover:from-[#115e59] hover:to-[#065f46] shadow-sm hover:shadow-md transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer mt-2"
							type="submit"
							disabled={busy}
						>
							{busy ? (
								<>
									<Loader2 className="size-4 animate-spin" />
									<span>Updating password…</span>
								</>
							) : (
								<>
									<span>Update Password</span>
									<ArrowRight className="size-4" />
								</>
							)}
						</button>
					</form>
				</div>
			</div>
		</main>
	);
}
